import axios from 'axios'
import { setupMobileInterceptors } from './mobileAuthService.js'
import { USE_JWT_COOKIE } from '../../shared/auth/authConfig.js'

/**
 * APIベースURL
 * モバイルオブジェクト詳細用のAPIエンドポイント
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

/**
 * モバイルオブジェクト詳細用のaxiosインスタンス
 * 共通の設定とインターセプターが適用されたHTTPクライアント
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// モバイルオブジェクト詳細用のインターセプターを設定
setupMobileInterceptors(apiClient, 'Mobile Object Detail')

/**
 * オブジェクト詳細情報を取得
 * 指定されたオブジェクトIDの詳細情報を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} categoryId - 撮影メモカテゴリーID（オプション）
 * @returns {Promise<Object>} オブジェクト詳細情報
 */
const getObjectDetail = async (objectId, categoryId) => {
  const data = {
    objectId: objectId,
    zsatsueiMemoCategoryId: categoryId || null,
    changeSatsueiMemoFlg: (categoryId ? true : false)
  };

  const response = await apiClient.post('/getObject', data);
  return response.data;
}

/**
 * オブジェクト画像一覧を取得
 * 指定されたオブジェクトの画像一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {number} imageFolderId - 画像フォルダーID（デフォルト: 99 = 全フォルダ）
 * @returns {Promise<Object>} 画像一覧データ
 */
const getObjectImageList = async(objectId, imageFolderId = 99) => {
  const data = {
    objectId: objectId
  };

  // 全フォルダ表示でない場合はimageFolderIdを設定
  if (imageFolderId !== 99) {
    data.imageFolderId = imageFolderId;
  }
  
  const response = await apiClient.post('/getObjectImageList', data);
  return response.data;
}

/**
 * 撮影メモカテゴリー一覧を取得
 * 撮影メモのカテゴリー一覧を取得
 * @returns {Promise<Object>} カテゴリー一覧データ
 */
const getSatsueiMemoCategories = async () => {
  const response = await apiClient.post('/getSatsueiMemoCategory', {});
  return response.data;
}

/**
 * 撮影ガイド一覧を取得
 * 指定されたカテゴリーの撮影ガイド一覧を取得
 * @param {string} categoryId - 撮影メモカテゴリーID
 * @returns {Promise<Object>} 撮影ガイド一覧データ
 */
const getSatsueiGuides = async (categoryId) => {
  const response = await apiClient.post('/getSatsueiGuideList', {
    satsueiMemoCategoryId: categoryId
  });
  return response.data;
}

/**
 * 拠点一覧を取得
 * システム内の拠点一覧を取得
 * @returns {Promise<Object>} 拠点一覧データ
 */
const getKyotenLists = async () => {
  const response = await apiClient.post('/getKyotenList', {});
  return response.data;
}

/**
 * 保存フォルダ一覧を取得
 * 画像保存用のフォルダー一覧を取得
 * @returns {Promise<Object>} フォルダー一覧データ
 */
const getImageFolders = async () => {
  const response = await apiClient.post('/getImageFolderList', {});
  return response.data;
}

/**
 * オブジェクトを新規登録
 * 新しいオブジェクトをシステムに登録
 * @param {Object} formData - オブジェクト登録データ
 * @returns {Promise<Object>} 登録結果
 */
const addObject = async (formData) => {
  const response = await apiClient.post('/addObject', formData);
  return response.data;
}

/**
 * オブジェクトを更新
 * 既存のオブジェクト情報を更新
 * @param {Object} formData - オブジェクト更新データ
 * @returns {Promise<Object>} 更新結果
 */
const updateObject = async (formData) => {
  const response = await apiClient.post('/updateObject', formData);
  return response.data;
}

/**
 * 動画一覧を取得
 * 指定されたオブジェクトの動画一覧を取得
 * @param {string} objectId - オブジェクトID
 * @returns {Promise<Object>} 動画一覧データ
 */
const getMovieList = async (objectId) => {
  const response = await apiClient.post('/getMovieList', {
    objectId: objectId
  });
  return response.data;
}

/**
 * オブジェクトをコピー
 * 既存のオブジェクトを新しいオブジェクトとしてコピー
 * @param {string} objectId - コピー元オブジェクトID
 * @param {string} copyDestinationObjectId - コピー先オブジェクトID
 * @param {string} satsueiMemoCategory - 撮影メモカテゴリーID
 * @returns {Promise<Object>} コピー結果
 */
const copyObject = async (objectId, copyDestinationObjectId, satsueiMemoCategory) => {
  const response = await apiClient.post('/copyObjectData', {
    copySourceObjectId: objectId,
    copyDestinationObjectId: copyDestinationObjectId,
    satsueiMemoCategoryId: satsueiMemoCategory
  });
  return response.data;
}

/**
 * 次のオブジェクトIDを取得
 * コピー用の新しいオブジェクトIDを取得
 * @returns {Promise<string>} 新しいオブジェクトID
 */
const getMaxCopyObjectId = async () => {
  const response = await apiClient.post('/getMaxObjectId', {});
  return response.data.objectId;
}

/**
 * オブジェクト数を取得
 * 指定されたオブジェクトの関連データ数を取得
 * @param {string} objectId - オブジェクトID
 * @returns {Promise<number>} オブジェクト数
 */
const getObjectCount = async (objectId) => {
  const response = await apiClient.post('/getObjectCount', {
    objectId: objectId
  });
  return response.data;
}

/**
 * オブジェクトを削除
 * 指定されたオブジェクトを削除
 * @param {string} objectId - オブジェクトID
 * @returns {Promise<Object>} 削除結果
 */
const deleteObject = async (objectId) => {
  const response = await apiClient.post('/deleteObject', {
    objectId: objectId
  });
  return response.data;
}

/**
 * 画像を削除
 * 指定された画像を削除
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @returns {Promise<Object>} 削除結果
 */
const deleteImage = async (objectId, objectImageId) => {
  const response = await apiClient.post('/removeImage', {
    objectId: objectId,
    objectImageId: objectImageId
  });
  return response.data;
}

/**
 * 画像順序を更新
 * 画像の表示順序を更新
 * @param {string} objectId - オブジェクトID
 * @param {Array} updateImageOrderIndexs - 画像順序インデックス配列
 * @returns {Promise<Object>} 更新結果
 */
const updateImageOrder = async (objectId, updateImageOrderIndexs) => {
  const response = await apiClient.post('/updateImageOrderIndex', {
    objectId: objectId,
    updateImageOrderIndexs: updateImageOrderIndexs
  });
  return response.data;
}

/**
 * 動画を削除
 * 指定された動画を削除
 * @param {string} objectId - オブジェクトID
 * @param {string} objectMovieId - 動画ID
 * @returns {Promise<Object>} 削除結果
 */
const deleteMovie = async (objectId, objectMovieId) => {
  const response = await apiClient.post('/removeMovie', {
    objectId: objectId,
    objectMovieId: objectMovieId
  });
  return response.data;
}

/**
 * オブジェクトシェア用のURLを取得
 * オブジェクトの共有用URLを生成・登録
 * @param {string} objectId - オブジェクトID
 * @returns {Promise<Object>} 共有URL情報
 */
const addObjectShare = async (objectId) => {
  const response = await apiClient.post('/addObjectShare', {
    objectId: objectId,
    shareable: "true"  // 共有可能フラグ
  });
  return response.data;
}

/**
 * 画像タグ一覧を取得
 * 指定された画像のタグ一覧を取得
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @returns {Promise<Object>} タグ一覧データ
 */
const getObjectImageTag = async (objectId, objectImageId) => {
  const response = await apiClient.post('/getObjectImageTag', {
    objectId: objectId,
    objectImageId: objectImageId
  });
  return response.data;
}

/**
 * 画像タグを全削除→登録
 * 既存のタグを全削除して新しいタグを登録
 * @param {Object} newTagData - 新しいタグデータ
 * @returns {Promise<Object>} 登録結果
 */
const addObjectImageTag = async (newTagData) => {
  const response = await apiClient.post('/addObjectImageTag', newTagData, {});
  return response.data;
}

/**
 * 画像をblobとして取得
 * 指定された画像をblob形式で取得し、URLオブジェクトとして返す
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @param {string} objectImageEditId - 画像編集ID
 * @param {string} filenameExtension - ファイル拡張子
 * @param {boolean} isUploaded - アップロード済みフラグ
 * @returns {Promise<string>} 画像URLまたはエラー画像パス
 */
const getImageAsBlob = async (objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded) => {
  if (objectImageId === undefined || objectImageId === null || objectImageId === '') {
    return '/core/img/errorimage.png'
  }

  try {
    const response = await apiClient.get('/getImage', {
      params: {
        objectId,
        objectImageId,
        objectImageEditId,
        filenameExtension,
        isUploaded,
        type: 'thumb_comp',    // サムネイル圧縮タイプ
        width: 148,            // 幅
        height: 148           // 高さ
      },
      responseType: 'blob',
      headers: { Accept: 'image/jpeg' }
    })
    return URL.createObjectURL(response.data)
  } catch (error) {
    return '/core/img/errorimage.png'
  }
}

/**
 * 画像編集IDの最大値を取得
 * 指定された画像の次の編集IDを取得
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @returns {Promise<Object>} 編集ID情報
 */
const getMaxImageEditId = async (objectId, objectImageId) => {
  const response = await apiClient.post('/getMaxImageEditId', {
    objectId: objectId,
    objectImageId: objectImageId
  });
  return response.data;
}

/**
 * 編集用の画像をblobとして取得
 * 手書き編集用にフルサイズの画像をblob形式で取得
 * @param {string} objectId - オブジェクトID
 * @param {string} objectImageId - 画像ID
 * @param {string} objectImageEditId - 画像編集ID
 * @param {string} filenameExtension - ファイル拡張子
 * @returns {Promise<Object>} 画像blobレスポンス
 */
const getImageBlobForEdit = async (objectId, objectImageId, objectImageEditId, filenameExtension) => {
  try {
    const params = {
      objectId,
      objectImageId,
      objectImageEditId,
      filenameExtension,
      type: 'satsueiImage',
      time: new Date().getTime()
    }

    const response = await apiClient.get('/getImage', {
      params,
      responseType: 'blob',
      headers: { Accept: 'image/jpeg' }
    })
    return response
  } catch (error) {
    console.error('Failed to get image blob for edit:', error)
    throw error
  }
}

/**
 * 編集済み画像をアップロード
 * 手書きを追加した画像をサーバーにアップロード
 * @param {FormData} formData - アップロード用のFormData
 * @returns {Promise<Object>} アップロード結果
 */
const uploadEditedImage = async (formData) => {
  const response = await apiClient.post('/uploadImage', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * モバイルオブジェクト詳細サービス
 * オブジェクトの詳細情報取得、画像・動画管理、シェア機能などのオブジェクト関連機能を提供
 */
export const mobileObjectDetailService = {
  // === オブジェクト基本操作 ===
  getObjectDetail,           // オブジェクト詳細情報取得
  addObject,                // オブジェクト新規登録
  updateObject,             // オブジェクト更新
  deleteObject,             // オブジェクト削除
  copyObject,               // オブジェクトコピー
  getMaxCopyObjectId,       // 次のオブジェクトID取得
  getObjectCount,           // オブジェクト数取得

  // === 画像・動画管理 ===
  getObjectImageList,       // 画像一覧取得
  deleteImage,              // 画像削除
  updateImageOrder,         // 画像順序更新
  getMovieList,             // 動画一覧取得
  deleteMovie,              // 動画削除
  getImageAsBlob,           // 画像をblobとして取得
  getMaxImageEditId,        // 画像編集IDの最大値を取得
  getImageBlobForEdit,      // 編集用の画像をblobとして取得
  uploadEditedImage,        // 編集済み画像をアップロード

  // === メタデータ取得 ===
  getSatsueiMemoCategories, // 撮影メモカテゴリー一覧取得
  getSatsueiGuides,         // 撮影ガイド一覧取得
  getKyotenLists,           // 拠点一覧取得
  getImageFolders,          // 保存フォルダ一覧取得

  // === シェア・タグ機能 ===
  addObjectShare,           // オブジェクトシェアURL取得
  getObjectImageTag,        // 画像タグ一覧取得
  addObjectImageTag,        // 画像タグ登録
}
